/**
 * Greeting rank — the monotonic "which welcome copy is better" order for the
 * per-thread greeting (2026-08-09 multi-tab greeting parity).
 *
 * The same new thread used to show DIFFERENT greetings in different tabs of
 * one account: each tab composed from ITS OWN tenant-config load state, and
 * the stale-greeting reconciliation (useChatThreads) swapped the persisted
 * copy for whatever the local composer produced — symmetric last-writer-wins
 * with no notion of which copy is better, so two tabs settled on different
 * configs could fight forever (legacy tenant copy vs the personalized one).
 *
 * The fix: every composed greeting carries a RANK derived from where its copy
 * came from, the seeded welcome message persists it, and the reconciliation
 * replaces the persisted greeting only on a STRICTLY HIGHER rank. That makes
 * the persisted greeting monotonic — the fixpoint every tab converges on is
 * the most-personalized copy any tab has ever computed.
 *
 * Rank order (see computeGreetingRank):
 *   0  unknown/legacy (a stored greeting minted before ranking existed)
 *   1  guest copy / error fallback (identity or config unsettled)
 *   2  generic in-code fallback (also the default when a host passes no source)
 *   3  tenant-level Firestore override / firm-aware in-code fallback (peers:
 *      neither provably better — ties never swap, so panes stay stable)
 *   4  account-level onboarding copy (accounts/{uid}/config/runtime)
 *   +2 when memory-distilled suggestions were folded into the composed text —
 *      the personalized-with-suggestions greeting always wins.
 *
 * Pure module — no React, no firebase — so it is trivially unit-testable and
 * importable from both the config hook and the thread store.
 */

/** Where the active welcome copy came from (resolved by useTenantChatConfig). */
export type WelcomeSource = 'guest' | 'error' | 'generic' | 'firm' | 'tenant' | 'account';

export const GREETING_SOURCE_RANK: Record<WelcomeSource, number> = {
  guest: 1,
  error: 1,
  generic: 2,
  firm: 3,
  tenant: 3,
  account: 4,
};

/** Rank boost when the composed greeting actually used memory suggestions. */
export const GREETING_SUGGESTIONS_RANK_BOOST = 2;

/**
 * Rank a composed greeting. An unknown/absent source ranks as 'generic' (2):
 * hosts that predate the source field behave exactly like the old
 * unconditional reconciliation against unranked (0) stored copies, but can
 * never downgrade a copy a source-aware tab has already persisted.
 */
export function computeGreetingRank(
  source: WelcomeSource | string | undefined,
  usedSuggestions: boolean,
): number {
  const base =
    source && Object.prototype.hasOwnProperty.call(GREETING_SOURCE_RANK, source)
      ? GREETING_SOURCE_RANK[source as WelcomeSource]
      : GREETING_SOURCE_RANK.generic;
  return usedSuggestions ? base + GREETING_SUGGESTIONS_RANK_BOOST : base;
}

/** Rank persisted on a stored greeting message; 0 for pre-ranking copies. */
export function storedGreetingRank(message: { greetingRank?: unknown } | null | undefined): number {
  const rank = (message as { greetingRank?: unknown } | null | undefined)?.greetingRank;
  return typeof rank === 'number' && Number.isFinite(rank) && rank > 0 ? rank : 0;
}

/**
 * Whether a rank band's copy derives from ONE shared truth, making same-rank
 * replacement CONVERGENT (2026-08-09 hotfix — the strict tie-freeze broke
 * same-source refinement: onboarding writes a placeholder account welcome and
 * refines it minutes later at the SAME rank; a frozen tie stranded the
 * placeholder forever — the exact 2026-08-04 stale-greeting bug, reborn).
 *
 * Convergent bands: guest/error (1) and generic (2) are deterministic in-code
 * copy keyed only on the shared UI language; account (4) and
 * account+suggestions (6) mirror the account's own runtime doc, which every
 * tab live-subscribes to — tie-swaps chase the doc and settle. The AMBIGUOUS
 * bands stay frozen on ties: 3 (tenant-override vs firm-fallback) and 5
 * (either + suggestions) are two DIFFERENT sources at one rank — the exact
 * pair whose symmetric swap war this ranking exists to end.
 */
export function isConvergentGreetingRank(rank: number): boolean {
  return rank > 0 && rank !== GREETING_SOURCE_RANK.tenant
    && rank !== GREETING_SOURCE_RANK.tenant + GREETING_SUGGESTIONS_RANK_BOOST;
}
